/**
 * Panneau de propriétés pour éditer un champ sélectionné
 */

import { FieldConfig, FontWeight, TextAlign, BarcodeType, ZebraFont } from '../../types/label-templates.types';

interface Props {
  field: FieldConfig | null;
  onUpdate: (updates: Partial<FieldConfig>) => void;
  onDelete: () => void;
}

export function FieldPropertiesPanel({ field, onUpdate, onDelete }: Props) {
  if (!field) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 h-full">
        <div className="flex flex-col items-center justify-center h-full text-center text-gray-400">
          <svg className="w-16 h-16 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
          </svg>
          <p className="text-sm font-medium">Aucun champ sélectionné</p>
          <p className="text-xs mt-1">Cliquez sur un champ du canvas pour l'éditer</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900">Propriétés du champ</h3>
        <button
          onClick={onDelete}
          className="text-red-600 hover:text-red-700 text-xs font-medium"
          title="Supprimer ce champ"
        >
          🗑️ Supprimer
        </button>
      </div>

      <div className="space-y-4">
        {/* Label */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Label
          </label>
          <input
            type="text"
            value={field.label || ''}
            onChange={(e) => onUpdate({ label: e.target.value })}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Nom d'affichage"
          />
        </div>

        {/* Position */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Position X (mm)
            </label>
            <input
              type="number"
              step="0.5"
              min="0"
              value={field.x}
              onChange={(e) => onUpdate({ x: parseFloat(e.target.value) || 0 })}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Position Y (mm)
            </label>
            <input
              type="number"
              step="0.5"
              min="0"
              value={field.y}
              onChange={(e) => onUpdate({ y: parseFloat(e.target.value) || 0 })}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        {/* Ordre d'affichage */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Z-Index (ordre)
          </label>
          <input
            type="number"
            min="0"
            value={field.zIndex}
            onChange={(e) => onUpdate({ zIndex: parseInt(e.target.value) || 0 })}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <p className="text-xs text-gray-500 mt-1">Plus élevé = au-dessus</p>
        </div>

        {/* Texte libre éditable */}
        {field.source === 'freetext' && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Texte à afficher
            </label>
            <textarea
              value={field.freeText || ''}
              onChange={(e) => onUpdate({ freeText: e.target.value })}
              placeholder="Saisissez votre texte..."
              rows={3}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
            />
            <p className="mt-1 text-xs text-gray-500">
              Ce texte sera imprimé tel quel sur l'étiquette
            </p>
          </div>
        )}

        {/* Propriétés selon le type */}
        {field.type !== 'qr' && field.type !== 'barcode' && field.type !== 'badge' && (
          <>
            {/* Taille de police */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Taille de police (pt)
              </label>
              <input
                type="number"
                min="6"
                max="48"
                value={field.fontSize || 10}
                onChange={(e) => onUpdate({ fontSize: parseInt(e.target.value) || 10 })}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <div className="flex gap-1 mt-1">
                {[8, 10, 12, 14, 16, 20, 24].map((size) => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => onUpdate({ fontSize: size })}
                    className={`px-2 py-0.5 text-xs rounded border ${
                      field.fontSize === size
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            {/* Famille de police */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Famille de police
              </label>
              <select
                value={field.fontFamily || '0'}
                onChange={(e) => onUpdate({ fontFamily: e.target.value as ZebraFont })}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="0">Zebra Standard (0)</option>
                <option value="A">ULD 9×5 (A)</option>
                <option value="B">ULD 11×7 (B)</option>
                <option value="D">OCR-B (D)</option>
                <option value="E">OCR-A (E)</option>
                <option value="F">Gothic Bold (F)</option>
                <option value="G">Gothic (G)</option>
                <option value="H">Swiss Bold (H)</option>
                <option value="P">Swiss Roman (P)</option>
              </select>
            </div>

            {/* Graisse */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Graisse
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => onUpdate({ fontWeight: 'normal' as FontWeight })}
                  className={`flex-1 px-3 py-1.5 text-xs rounded border ${
                    field.fontWeight === 'normal'
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
                  }`}
                >
                  Normal
                </button>
                <button
                  type="button"
                  onClick={() => onUpdate({ fontWeight: 'bold' as FontWeight })}
                  className={`flex-1 px-3 py-1.5 text-xs font-bold rounded border ${
                    field.fontWeight === 'bold'
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
                  }`}
                >
                  Gras
                </button>
              </div>
            </div>

            {/* Alignement */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Alignement
              </label>
              <div className="flex gap-2">
                {(['left', 'center', 'right'] as TextAlign[]).map((align) => (
                  <button
                    key={align}
                    type="button"
                    onClick={() => onUpdate({ align })}
                    className={`flex-1 px-3 py-1.5 text-xs rounded border ${
                      field.align === align
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
                    }`}
                  >
                    {align === 'left' ? '◀ Gauche' : align === 'center' ? '▬ Centre' : '▶ Droite'}
                  </button>
                ))}
              </div>
            </div>

            {/* Préfixe/Suffixe */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Préfixe
                </label>
                <input
                  type="text"
                  value={field.prefix || ''}
                  onChange={(e) => onUpdate({ prefix: e.target.value })}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ex: €"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Suffixe
                </label>
                <input
                  type="text"
                  value={field.suffix || ''}
                  onChange={(e) => onUpdate({ suffix: e.target.value })}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ex: /kg"
                />
              </div>
            </div>
          </>
        )}

        {/* Propriétés QR Code */}
        {field.type === 'qr' && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Taille QR Code (mm)
            </label>
            <input
              type="number"
              min="10"
              max="50"
              value={field.qrSize || 15}
              onChange={(e) => onUpdate({ qrSize: parseInt(e.target.value) || 15 })}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        )}

        {/* Propriétés Code-barres */}
        {field.type === 'barcode' && (
          <>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Type de code-barres
              </label>
              <select
                value={field.barcodeType || 'CODE128'}
                onChange={(e) => onUpdate({ barcodeType: e.target.value as BarcodeType })}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="EAN13">EAN13</option>
                <option value="CODE128">CODE128</option>
                <option value="CODE39">CODE39</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Hauteur (mm)
              </label>
              <input
                type="number"
                min="5"
                max="30"
                value={field.barcodeHeight || 10}
                onChange={(e) => onUpdate({ barcodeHeight: parseInt(e.target.value) || 10 })}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={field.showBarcodeText !== false}
                  onChange={(e) => onUpdate({ showBarcodeText: e.target.checked })}
                  className="h-3 w-3 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <span className="ml-2 text-xs text-gray-700">Afficher le texte</span>
              </label>
            </div>
          </>
        )}

        {/* Propriétés Badge */}
        {field.type === 'badge' && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Taille de police (pt)
            </label>
            <input
              type="number"
              min="6"
              max="24"
              value={field.fontSize || 8}
              onChange={(e) => onUpdate({ fontSize: parseInt(e.target.value) || 8 })}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        )}

        {/* Format de date */}
        {field.type === 'date' && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Format de date
            </label>
            <select
              value={field.format || 'DD/MM/YYYY'}
              onChange={(e) => onUpdate({ format: e.target.value })}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              <option value="DD/MM/YY">DD/MM/YY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            </select>
          </div>
        )}

        {/* État actif */}
        <div className="pt-4 border-t border-gray-200">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={field.enabled}
              onChange={(e) => onUpdate({ enabled: e.target.checked })}
              className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <span className="ml-2 text-xs font-medium text-gray-700">Champ visible sur l'étiquette</span>
          </label>
          {!field.enabled && (
            <p className="mt-2 text-xs text-yellow-700 bg-yellow-50 p-2 rounded">
              ⚠️ Ce champ est désactivé. Cochez la case ci-dessus pour le rendre visible.
            </p>
          )}
        </div>
      </div>

      {/* Info sur le champ */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="text-xs text-gray-500 space-y-1">
          <div><span className="font-medium">ID:</span> {field.id}</div>
          <div><span className="font-medium">Source:</span> {field.source}</div>
          <div><span className="font-medium">Type:</span> {field.type}</div>
          {field.field && <div><span className="font-medium">Champ:</span> {field.field}</div>}
        </div>
      </div>
    </div>
  );
}
